import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import { STAGE, type ElementOf, type SectionDto } from '@atnt/shared';
import { cn } from '@/shared/lib/cn';
import { DataIcon } from '../DataIcon';
import { formatCount } from '../live/format';
import { useLocale } from '../live/values';
import { dp } from '../units';
import { useScene } from './scene';

/** Bottom margin of a docked grid (14:7461: 1076 − 797 − 244 = 35). */
export const SECTION_GRID_MARGIN_BOTTOM = 35;

export interface SectionGridLayout {
  x: number;
  y: number;
  width: number;
  height: number;
  rows: number;
  columns: number;
  cardWidth: number;
  cardHeight: number;
  gap: number;
}

/**
 * Geometry of the "Bo'limlar" card grid in stage px (§4.9). Docked grids are anchored to the stage bottom and
 * grow upwards; free grids use the element's (x, y). Shared by the viewer and the editor (hit-testing).
 */
export function sectionGridLayout(el: ElementOf<'sectionGrid'>, cardCount: number, stageHeight: number = STAGE.height): SectionGridLayout {
  const columns = Math.max(1, Math.round(el.columns));
  const rows = cardCount > 0 ? Math.ceil(cardCount / columns) : 1;
  const cardWidth = (el.width - (columns - 1) * el.gap) / columns;
  const height = rows * el.cardHeight + (rows - 1) * el.gap;
  const y = el.dock === 'bottom' ? stageHeight - SECTION_GRID_MARGIN_BOTTOM - height : el.y;
  return { x: el.x, y, width: el.width, height, rows, columns, cardWidth, cardHeight: el.cardHeight, gap: el.gap };
}

/** Count shown on a card: manual or auto (distinct devices pinned in the section's folders). */
export const sectionCount = (s: SectionDto) => (s.countSource === 'manual' ? (s.manualCount ?? 0) : s.autoCount);

export function SectionGridView({ element, children }: { element: ElementOf<'sectionGrid'>; children?: ReactNode }) {
  const { sections, mode } = useScene();
  const { t } = useTranslation('constructor');
  const cards = sections.filter((s) => !s.hidden);
  const layout = sectionGridLayout(element, cards.length);

  return (
    <div
      data-element-id={element.id}
      data-element-type="sectionGrid"
      className="absolute grid"
      style={{
        left: dp(layout.x),
        top: dp(layout.y),
        width: dp(layout.width),
        gridTemplateColumns: `repeat(${layout.columns}, minmax(0, 1fr))`,
        gridAutoRows: dp(layout.cardHeight),
        gap: dp(layout.gap),
      }}
    >
      {cards.map((s) => (
        <SectionCard key={s.id} section={s} />
      ))}
      {!cards.length && mode === 'edit' && (
        <div
          className="flex items-center justify-center rounded-12 border border-dashed border-white-300 font-sans text-13 text-white-600"
          style={{ gridColumn: `1 / span ${layout.columns}` }}
        >
          {t('elements.sectionsPlaceholder')}
        </div>
      )}
      {children}
    </div>
  );
}

export function SectionCard({ section, className }: { section: SectionDto; className?: string }) {
  const { variant, mode, onOpenSection } = useScene();
  const { t } = useTranslation('constructor');
  const locale = useLocale();
  const nameOnly = section.cardMode === 'nameOnly';
  const icon = section.icon.kind === 'preset' ? section.icon.name : null;
  const clickable = Boolean(onOpenSection) && mode !== 'edit';
  const Tag = clickable ? 'button' : 'div';
  const tagProps = clickable ? { type: 'button' as const, onClick: () => onOpenSection?.(section.id) } : {};
  const count = formatCount(sectionCount(section), locale);

  const iconNode =
    section.icon.kind === 'upload' ? (
      <SectionIconImage section={section} className="size-24" />
    ) : (
      <DataIcon name={icon} className="size-24" />
    );

  // 14:7285 / 317:6938 "Glass Card" (viewer and constructor share it): White/100 + White/400 stroke, r24, blur 24,
  // padding 16; icon wrapper 48 r14 (section colour) + icon 24, gap 14; name Medium 14/20 White/600 (up to 2 lines);
  // count ExtraBold 20/28 + unit Regular 14/20 White/600, gap 4 below the name
  return (
    <Tag
      {...tagProps}
      data-section-id={section.id}
      className={cn(
        'glass-card flex min-w-0 items-center gap-14 overflow-hidden p-16 text-left font-sans transition-colors',
        clickable && 'cursor-pointer hover:bg-white-200 focus-visible:bg-white-200',
        variant === 'viewer' && 'text-white',
        className,
      )}
    >
      <span className="flex size-48 shrink-0 items-center justify-center rounded-14 text-white" style={{ backgroundColor: section.color }}>
        {iconNode}
      </span>
      <span className="flex min-w-0 flex-col gap-4">
        <span className={cn('text-sm font-medium tracking-normal text-white-600', nameOnly ? 'line-clamp-2' : 'truncate')}>{section.name}</span>
        {!nameOnly && (
          <span className="flex items-baseline gap-4 whitespace-nowrap">
            <span className="text-xl font-extrabold text-white">{count}</span>
            <span className="text-sm text-white-600">{t('common.pcs')}</span>
          </span>
        )}
      </span>
    </Tag>
  );
}

function SectionIconImage({ section, className }: { section: SectionDto; className?: string }) {
  const { fileUrl } = useScene();
  if (section.icon.kind !== 'upload') return null;
  return <img src={fileUrl(section.icon.file.fileId)} alt="" draggable={false} className={cn('object-contain', className)} />;
}
